import { useContext, useEffect, useMemo, useState } from 'react';
import { differenceInMinutes } from 'date-fns';
import { useTimeline } from '../../hooks/recipes/useTimeline';
import { AuthContext } from '../../context/AuthContext';
import { useRecipeDisplay } from '../../context/RecipeDisplayContext';
import { getProductionRunStatus } from '../../utils/recipes/recipeControl';
import { EVENT_NAMES } from '../../utils/eventLogging/eventNames';
import { useTranslation } from '../../i18n/i18n';
import { formatMeasureLabel } from '../../utils/recipes/numberFormatUtils';
import { useRecipeDisplayEnrichment } from '../../hooks/recipes/useRecipeDisplayEnrichment';
import ProductionRunCard from './components/ProductionRunCard';
import IngredientFieldTags from './components/IngredientFieldTags';
import ProductionCorrectionsModal from './components/ProductionCorrectionsModal';
import ProductionInstructionModal from './components/ProductionInstructionModal';
import ProductionRunDetailsModal from './components/ProductionRunDetailsModal';
import { cn } from '../../utils/cn';
import EllipseTooltip from '../../utils/EllipseTooltip';

export default function RecipeDisplay({ deviceName, nColumns = 3 }) {
  const { currentUser, logEventCustom } = useContext(AuthContext);
  const languageCode = currentUser?.info?.languageCode;
  const { t } = useTranslation(languageCode);
  const [now, setNow] = useState(new Date());
  const [selectedProductionRun, setSelectedProductionRun] = useState(null);
  const [forcedCorrectionContext, setForcedCorrectionContext] = useState(null);
  const [forcedInstructionContext, setForcedInstructionContext] = useState(null);

  // All recipe/correction data (incl. normalized display items) comes from the context layer.
  const { currentRunningProduction, displayItems, productionRuns, productionEvents, shiftInfo, settings } =
    useRecipeDisplay();

  const { forcedCorrection, enrichedSelectedProductionRunLive } = useRecipeDisplayEnrichment({
    productionRuns,
    productionEvents,
    selectedProductionRun,
    forcedCorrectionContext,
  });

  useEffect(() => {
    if (!forcedCorrectionContext) return;
    if (!forcedCorrection) setForcedCorrectionContext(null);
  }, [forcedCorrectionContext, forcedCorrection]);

  const forcedInstruction = useMemo(() => {
    if (!forcedInstructionContext?.instructionId) return null;
    return productionEvents.find((e) => e.id === forcedInstructionContext.instructionId) || null;
  }, [forcedInstructionContext?.instructionId, productionEvents]);

  useEffect(() => {
    if (!forcedInstructionContext) return;
    if (!forcedInstruction) setForcedInstructionContext(null);
  }, [forcedInstructionContext, forcedInstruction]);

  const { minDate, maxDate, totalDurationMinutes, timeMarkers, firstMarkerTime } = useTimeline(productionRuns, now);

  const nowOffset = differenceInMinutes(now, minDate);
  const currentTimePercent = (nowOffset / totalDurationMinutes) * 100;

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const markerStep = useMemo(() => {
    if (timeMarkers.length >= 20) return 3;
    if (timeMarkers.length >= 14) return 2;
    return 1;
  }, [timeMarkers.length]);

  const columns = useMemo(() => {
    return Math.max(nColumns, 3);
  }, [nColumns]);

  function formatHourLabel(label) {
    // Usually "HH:mm" (e.g., "11:00") -> show hour only ("11")
    if (typeof label !== 'string') return label;
    return parseInt(label.replace(':00', ''));
  }

  function handleProductionRunClick(productionRun) {
    logEventCustom(EVENT_NAMES.PRODUCTION_DETAILS_OPEN, {
      production_id: productionRun?.id || null,
      // Runs carry no status field; it is derived from the run's window (see getProductionRunStatus).
      production_status: getProductionRunStatus(productionRun),
    });

    setSelectedProductionRun(productionRun);
  }

  return (
    <div className='flex m-6 gap-4 text-light-text dark:text-dark-text mb-0'>
      <div className='w-full h-full box-border'>
        <div
          className={cn('grid h-[8.5rem] gap-3.5')}
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          <div className='relative rounded-xl overflow-hidden col-span-1 transition-all duration-200 border border-[#E8E8EA] dark:border-[#3C3E53] bg-[#F3F6F9] dark:bg-[#242933] p-3 box-border h-full'>
            <div className='relative overflow-hidden'>
              <div
                className='absolute z-[3] pointer-events-none -translate-x-1/2 top-[1.375rem] bottom-0'
                style={{ left: `${currentTimePercent}%` }}
              >
                {/* Line starts below hour labels (like original) */}
                <div className='absolute top-0 bottom-0 w-px bg-[#2563EB] dark:bg-[#22D3EE] -translate-x-1/2' />
                {/* Knob sits on the boundary (half above the line start) */}
                <div className='absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 size-2.5 rounded-full bg-[#2563EB] dark:bg-[#22D3EE]' />
              </div>

              <div className='h-[1.375rem] relative'>
                {timeMarkers
                  .filter((_, idx) => idx % markerStep === 0)
                  .map((marker) => {
                    const isLeftEdge = marker.leftPercent < 3;
                    const isRightEdge = marker.leftPercent > 97;
                    const translateClass = isLeftEdge ? '' : isRightEdge ? '-translate-x-full' : '-translate-x-1/2';

                    return (
                      <div key={marker.time.toISOString()} className='pointer-events-none'>
                        {/* Tick is always centered on the exact marker position */}
                        <div
                          className='absolute bottom-0 h-1.5 w-px bg-[#B9BAC0] dark:bg-[#2E3440] -translate-x-1/2'
                          style={{ left: `${marker.leftPercent}%` }}
                        />
                        {/* Label can edge-adjust without moving the tick */}
                        <div
                          className={`absolute top-0 text-xs font-semibold text-[#737580] dark:text-[#505267] transition-all duration-200 whitespace-nowrap ${translateClass}`}
                          style={{ left: `${marker.leftPercent}%` }}
                        >
                          {formatHourLabel(marker.label)}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {deviceName &&
                (() => {
                  return (
                    <div
                      key={deviceName}
                      className={`min-h-[5.625rem] box-border relative flex items-stretch rounded-lg border border-[#D0D1D5] bg-[#F6F6F7] dark:border-[#3C3E53] dark:bg-[#0F172A66]`}
                    >
                      {productionRuns.map((productionRun) => (
                        <ProductionRunCard
                          key={productionRun.id}
                          productionRun={productionRun}
                          minDate={minDate}
                          maxDate={maxDate}
                          firstMarkerTime={firstMarkerTime}
                          totalDurationMinutes={totalDurationMinutes}
                          // isCurrentProduction={productionRun.id === currentRunningProduction?.id}
                          onProductionRunClick={handleProductionRunClick}
                        />
                      ))}
                    </div>
                  );
                })()}
            </div>
          </div>
          <div
            className='rounded-xl transition-all duration-200 overflow-hidden flex flex-row border border-[#E8E8EA] dark:border-[#3C3E53] bg-[#F3F6F9] dark:bg-[#242933] h-full'
            style={{ gridColumn: `span ${columns - 1} / span ${columns - 1}` }}
          >
            {currentRunningProduction && displayItems.length > 0 ? (
              <div className='w-full h-full min-w-0 flex flex-row'>
                {displayItems.map((item, index) => {
                  const isCorrectedMaterial = item?.corrected === true;
                  return (
                    <div
                      key={`${item.ingredientId || 'ingredient'}-${index}`}
                      className='flex flex-col items-center justify-between gap-1.5 text-center flex-1 min-w-0 p-3 border-r border-[#E8E8EA] dark:border-[#3C3E53] last:border-r-0'
                    >
                      <div className='flex w-full min-w-0 flex-col items-center gap-1'>
                        <span
                          className={cn(
                            'text-xl font-medium',
                            isCorrectedMaterial
                              ? 'text-orange-500 dark:text-orange-400'
                              : 'text-[#16192C] dark:text-[#FAFAFA]',
                          )}
                        >
                          {formatMeasureLabel(item.value, item.unit, languageCode, t)}
                        </span>

                        <div className='w-full min-w-0 self-stretch leading-none'>
                          <EllipseTooltip
                            title={item.name || t('recipe_display_unknown_ingredient')}
                            className={cn(
                              'text-sm font-medium',
                              isCorrectedMaterial
                                ? 'text-orange-500 dark:text-orange-400'
                                : 'text-[#737580] dark:text-[#CAD5E2]',
                            )}
                          >
                            {item.name || t('recipe_display_unknown_ingredient')}
                          </EllipseTooltip>
                        </div>
                      </div>
                      <IngredientFieldTags fields={item.fields} noFieldsLabel={t('recipe_display_no_fields')} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className='text-base text-[#737580] dark:text-[#90A1B9] flex justify-center items-center h-full w-full'>
                <span>
                  {t(
                    currentRunningProduction
                      ? 'recipe_display_no_conveyor_ingredients'
                      : 'recipe_display_no_active_run',
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
        {enrichedSelectedProductionRunLive && (
          <ProductionRunDetailsModal
            productionRun={enrichedSelectedProductionRunLive}
            productionEvents={productionEvents}
            currentUser={currentUser}
            onClose={() => {
              setSelectedProductionRun(null);
            }}
            onCorrectionClick={(correction) => {
              setForcedCorrectionContext({ correctionId: correction.id });
            }}
            onInstructionClick={(instruction) => {
              setForcedInstructionContext({ instructionId: instruction.id });
            }}
          />
        )}
        <ProductionCorrectionsModal
          forcedCorrection={forcedCorrection}
          onClose={() => setForcedCorrectionContext(null)}
        />
        <ProductionInstructionModal
          currentRunningProduction={currentRunningProduction}
          productionEvents={productionEvents}
          shiftInfo={shiftInfo}
          settings={settings}
          forcedInstruction={forcedInstruction}
          onClose={() => setForcedInstructionContext(null)}
        />
      </div>
    </div>
  );
}
